(() => {
  const ROOT_SELECTOR = '[data-brands-root]';
  const CATEGORY_ORDER = {
    luxury: 1,
    niche: 2,
    popular: 3,
    arabic: 4,
    other: 5
  };

  const normalize = (value) =>
    String(value || '')
      .toLocaleLowerCase('pl')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const firstLetter = (value) => {
    const normalized = normalize(value);
    return normalized ? normalized.charAt(0).toUpperCase() : '#';
  };

  const initImageFallbacks = (root) => {
    root.querySelectorAll('.pfm6-card__logo img').forEach((image) => {
      if (image.dataset.fallbackBound === 'true') return;

      const showFallback = () => {
        image.closest('.pfm6-card__logo')?.classList.add('is-fallback');
        image.remove();
      };

      image.addEventListener('error', showFallback, { once: true });

      if (image.complete && image.naturalWidth === 0) {
        showFallback();
      }

      image.dataset.fallbackBound = 'true';
    });
  };

  const init = (root) => {
    if (!root || root.dataset.brandsInitialized === 'true') return;

    const els = {
      search: root.querySelector('#pfm6SearchInput'),
      clear: root.querySelector('#pfm6ClearSearch'),
      cats: root.querySelector('#pfm6CategoryFilters'),
      sort: root.querySelector('#pfm6SortSelect'),
      count: root.querySelector('#pfm6ResultsCount'),
      reset: root.querySelector('#pfm6ResetFilters'),
      featuredSection: root.querySelector('#pfm6FeaturedSection'),
      alpha: root.querySelector('#pfm6AlphabetFilters'),
      grid: root.querySelector('#pfm6BrandsGrid'),
      noResults: root.querySelector('#pfm6NoResults'),
      noResultsReset: root.querySelector('#pfm6NoResultsReset'),
      load: root.querySelector('#pfm6LoadMore')
    };

    if (!els.grid || !els.search || !els.cats || !els.sort) {
      root.dataset.brandsInitialized = 'true';
      initImageFallbacks(root);
      return;
    }

    const cards = [...els.grid.querySelectorAll('[data-brand-card]')];
    const perPage = Math.max(4, Number(root.dataset.perPage) || 12);
    let shown = perPage;
    let state = {
      q: '',
      category: 'all',
      letter: 'all',
      sort: 'name-asc'
    };

    const loadUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      const category = params.get('category');
      const letter = params.get('letter');
      const sort = params.get('sort');
      const availableCategories = new Set(
        [...els.cats.querySelectorAll('[data-category]')].map(
          (button) => button.dataset.category
        )
      );

      state = {
        q: q || '',
        category: category && availableCategories.has(category) ? category : 'all',
        letter: letter || 'all',
        sort: ['name-asc', 'name-desc', 'products', 'category'].includes(sort === 'popular' ? 'products' : sort)
          ? (sort === 'popular' ? 'products' : sort)
          : 'name-asc'
      };
    };

    const updateUrl = () => {
      const params = new URLSearchParams();

      if (state.q) params.set('q', state.q);
      if (state.category !== 'all') params.set('category', state.category);
      if (state.letter !== 'all') params.set('letter', state.letter);
      if (state.sort !== 'name-asc') params.set('sort', state.sort);

      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', nextUrl);
    };

    const initAlphabet = () => {
      const lettersInCatalog = new Set(
        cards.map((card) => firstLetter(card.dataset.brandName))
      );
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

      els.alpha.innerHTML = [
        '<button type="button" class="pfm6-letter is-active" data-letter="all" aria-pressed="true">Wszystkie</button>',
        ...letters.map((letter) => {
          const disabled = lettersInCatalog.has(letter) ? '' : ' disabled';
          return `<button type="button" class="pfm6-letter" data-letter="${letter}" aria-pressed="false"${disabled}>${letter}</button>`;
        })
      ].join('');
    };

    const getFilteredCards = () => {
      const q = normalize(state.q);

      return cards
        .filter((card) => {
          const name = normalize(card.dataset.brandName);
          const matchesSearch = !q || name.includes(q);
          const matchesCategory =
            state.category === 'all' || card.dataset.category === state.category;
          const matchesLetter =
            state.letter === 'all' || firstLetter(card.dataset.brandName) === state.letter;

          return matchesSearch && matchesCategory && matchesLetter;
        })
        .sort((a, b) => {
          const nameA = a.dataset.brandName || '';
          const nameB = b.dataset.brandName || '';

          if (state.sort === 'name-desc') {
            return nameB.localeCompare(nameA, 'pl', { sensitivity: 'base' });
          }

          if (state.sort === 'products') {
            const productCountDifference =
              Number(b.dataset.products || 0) - Number(a.dataset.products || 0);
            return productCountDifference || nameA.localeCompare(nameB, 'pl', { sensitivity: 'base' });
          }

          if (state.sort === 'category') {
            const categoryDifference =
              (CATEGORY_ORDER[a.dataset.category] || 99) -
              (CATEGORY_ORDER[b.dataset.category] || 99);
            return categoryDifference || nameA.localeCompare(nameB, 'pl', { sensitivity: 'base' });
          }

          return nameA.localeCompare(nameB, 'pl', { sensitivity: 'base' });
        });
    };

    const syncControls = () => {
      root.querySelectorAll('.pfm6-chip[data-category]').forEach((button) => {
        const active = button.dataset.category === state.category;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      root.querySelectorAll('.pfm6-letter[data-letter]').forEach((button) => {
        const active = button.dataset.letter === state.letter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      els.search.value = state.q;
      els.clear.hidden = !state.q;
      els.sort.value = state.sort;

      const isDefault =
        !state.q &&
        state.category === 'all' &&
        state.letter === 'all' &&
        state.sort === 'name-asc';

      if (els.reset) els.reset.hidden = isDefault;
    };

    const render = ({ updateHistory = true } = {}) => {
      const filtered = getFilteredCards();
      const visibleCards = new Set(filtered.slice(0, shown));

      cards.forEach((card) => {
        card.hidden = !visibleCards.has(card);
      });

      filtered.forEach((card) => els.grid.appendChild(card));
      cards.filter((card) => !filtered.includes(card)).forEach((card) => els.grid.appendChild(card));

      els.grid.hidden = filtered.length === 0;
      if (els.noResults) els.noResults.hidden = filtered.length > 0;
      if (els.load) els.load.hidden = filtered.length <= shown;

      if (els.count) {
        els.count.textContent = filtered.length
          ? `Pokazujemy ${Math.min(shown, filtered.length)} z ${filtered.length} marek.`
          : 'Brak marek dla wybranych kryteriów.';
      }

      const hideFeatured =
        Boolean(state.q) || state.category !== 'all' || state.letter !== 'all';
      if (els.featuredSection) els.featuredSection.hidden = hideFeatured;

      syncControls();
      if (updateHistory) updateUrl();
    };

    const resetAll = () => {
      state = {
        q: '',
        category: 'all',
        letter: 'all',
        sort: 'name-asc'
      };
      shown = perPage;
      render();
    };

    initAlphabet();
    loadUrl();
    initImageFallbacks(root);

    els.search.addEventListener('input', () => {
      state.q = els.search.value.trim();
      shown = perPage;
      render();
    });

    els.clear?.addEventListener('click', () => {
      state.q = '';
      shown = perPage;
      render();
      els.search.focus();
    });

    els.cats.addEventListener('click', (event) => {
      const button = event.target.closest('.pfm6-chip[data-category]');
      if (!button) return;

      state.category = button.dataset.category;
      shown = perPage;
      render();
    });

    els.alpha.addEventListener('click', (event) => {
      const button = event.target.closest('.pfm6-letter[data-letter]');
      if (!button || button.disabled) return;

      state.letter = button.dataset.letter;
      shown = perPage;
      render();
    });

    els.sort.addEventListener('change', () => {
      state.sort = els.sort.value;
      shown = perPage;
      render();
    });

    els.reset?.addEventListener('click', resetAll);
    els.noResultsReset?.addEventListener('click', resetAll);

    els.load?.addEventListener('click', () => {
      shown += perPage;
      render();
    });

    root.querySelectorAll('[data-pfm6-search]').forEach((button) => {
      button.addEventListener('click', () => {
        state.q = button.dataset.pfm6Search || '';
        state.category = 'all';
        state.letter = 'all';
        shown = perPage;
        render();
        els.search.focus();
      });
    });

    window.addEventListener('popstate', () => {
      loadUrl();
      shown = perPage;
      render({ updateHistory: false });
    });

    root.dataset.brandsInitialized = 'true';
    root.dataset.enhanced = 'true';
    render({ updateHistory: false });
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector(ROOT_SELECTOR));
  });
})();
